import { defineStore, acceptHMRUpdate } from 'pinia';
import utilitiesData from '../../utilities/common/data';
import destinationRepository from '../../repositories/destination/destinationRepository';
import mediaRepository from '../../repositories/general/mediaRepository';
import { useI18n } from 'vue-i18n';

const dataDestination = {
  dest_type_id: '',
  district_id: '',
  name: null,
  latitude: null,
  longitude: null,
  address: null,
  main_image: null,
  main_image_collect: [],
  is_superior: null,
  desc: null,
  facility: null,
  list_facility: [],
  list_facility_collect: [],
  list_label: [],
  list_label_collect: [],
  list_media: [],
  list_media_collect: [],
};

export const useDestinationStore = defineStore('destinationStore', {
  state: () => ({
    componentKey: 0,
    isSuccess: false,
    hasErrorSingle: null,
    // list page
    perpage: 20,
    search: null,
    page: 1,
    listData: [],
    pagination: {},
    isLoadingTable: false,
    // form page
    formData: structuredClone(dataDestination),
    hasFormError: structuredClone(dataDestination),
    isLoadingForm: false,
  }),
  actions: {
    async getData() {
      try {
        this.isLoadingTable = true;
        this.hasErrorSingle = null;
        this.isSuccess = false;

        const params = {
          search: this.search,
          page: this.page,
          perpage: this.perpage,
        };
        const dataDestination = await destinationRepository.getData(params);
        if (dataDestination.data.status === 200) {
          this.listData = dataDestination.data.data.destination.list;
          this.pagination = dataDestination.data.data.destination.pagination;
          this.isLoadingTable = false;
          this.componentKey++;
          this.isSuccess = true;
        }
      } catch (error) {
        console.log(error);
        this.isLoadingTable = false;
        this.isSuccess = false;
      }
    },
    async searchData() {
      this.page = 1;
      await this.getData();
    },
    async getDataByPaging() {
      await this.getData();
    },
    async saveData() {
      try {
        this.isLoadingForm = true;
        this.isSuccess = false;
        this.resetForm(false, false);

        const params = this.formData;
        const saveDestination = await destinationRepository.saveData(params);
        if (saveDestination.data.status === 200) {
          this.isSuccess = true;
          this.resetForm();
        }
      } catch (error) {
        console.log(error);
        this.setFormError(error.response.data.error);
        this.isSuccess = false;
        this.isLoadingForm = false;
      }
    },
    async getDataDetail(id) {
      this.isSuccess = false;

      const params = {
        destination_id: id,
      };
      const dataDetailDestination = await destinationRepository.getDataDetail(params);

      if (dataDetailDestination.data.status === 200) {
        this.isSuccess = true;
        const data = dataDetailDestination.data.data;

        this.formData.latitude = data.latitude;
        this.formData.longitude = data.longitude;
        this.formData.name = data.name;
        this.formData.dest_type_id = data.dest_type_id;
        this.formData.district_id = data.district_id;
        this.formData.address = data.address;
        this.formData.desc = data.desc;
        this.formData.is_superior = data.is_superior === 1 ? true : false;

        data.list_facility.forEach((item) => {
          this.formData.list_facility.push(item.facility);
          this.formData.list_facility_collect.push(item);
        });
        data.list_label.forEach((item) => {
          this.formData.list_label.push(item);
          this.formData.list_label_collect.push(item);
        });

        this.formData.main_image = data.main_image.file_id;
        this.formData.main_image_collect = data.main_image;

        data.list_media.forEach((item) => {
          this.formData.list_media.push(item.file_id);
          this.formData.list_media_collect.push(item);
        });
      }
    },
    async updateData(id) {
      try {
        this.isSuccess = false;
        this.isLoadingForm = true;
        this.resetForm(false, false);

        const params = {
          destination_id: id,
          ...this.formData,
        };
        const updateDestination = await destinationRepository.updateData(params);
        if (updateDestination.data.status === 200) {
          this.isSuccess = true;
          this.resetForm();
        }
      } catch (error) {
        console.log(error);
        this.setFormError(error.response.data.error);
        this.isSuccess = false;
        this.isLoadingForm = false;
      }
    },
    async deleteData(id) {
      try {
        this.isSuccess = false;

        const params = {
          destination_id: id,
        };
        const deleteDestination = await destinationRepository.deleteData(params);
        if (deleteDestination.data.status === 200) {
          this.isSuccess = true;
        }
      } catch (error) {
        console.log(error);
        this.isSuccess = false;
      }
    },
    async uploadFile(file, type) {
      try {
        this.isSuccess = false;
        this.isLoadingForm = true;
        this.resetForm(false, false);

        const uploadFile = await mediaRepository.uploadFile(file);
        if (uploadFile.data.status === 200) {
          this.isSuccess = true;
          if (type === 'MAIN_IMAGE') {
            this.formData.main_image = uploadFile.data.data.file_id;
            this.formData.main_image_collect = uploadFile.data.data;
          } else if (type === 'GALLERY_IMAGE') {
            this.formData.list_media.push(uploadFile.data.data.file_id);
            this.formData.list_media_collect.push(uploadFile.data.data);
          }
        }
      } catch (error) {
        console.log(error);
        this.isSuccess = false;
        this.setFormError(error.response.data.error);
      } finally {
        this.isLoadingForm = false;
      }
    },
    setFormError(error) {
      if (typeof error === 'string' || error instanceof String) {
        this.hasErrorSingle = error;
      } else {
        Object.keys(error).forEach((item) => {
          this.hasFormError[item] = error[item][0];
        });
      }
    },
    resetForm(withContent = true, withLoading = true) {
      if (withContent) {
        this.formData = structuredClone(dataDestination);
      }
      this.hasFormError = structuredClone(dataDestination);
      this.hasErrorSingle = null;
      if (withLoading) {
        this.isLoadingForm = false;
      }
    },
    removeErrorFormSingle(key) {
      this.hasFormError[key] = null;
    },
  },
  getters: {},
});

if (import.meta.webpackHot) {
  import.meta.webpackHot.accept(acceptHMRUpdate(useDestinationStore, import.meta.webpackHot));
}
